'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterBarProps {
  onFilterChange: (filters: FilterState) => void;
  activeFilters: FilterState;
}

export interface FilterState {
  category: string;
  language: string;
  mood: string;
  duration: string;
  searchQuery: string;
}

const FilterBar = ({ onFilterChange, activeFilters }: FilterBarProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const categories = [
    'All Stories',
    'Romance',
    'Thriller',
    'Comedy',
    'Drama',
    'Horror',
    'Mystery',
    'Regional',
    'Mythology',
    'Biography',
  ];

  const languages = [
    'All Languages',
    'Hindi',
    'English',
    'Tamil',
    'Telugu',
    'Bengali',
    'Marathi',
    'Gujarati',
    'Kannada',
    'Malayalam',
    'Punjabi',
  ];

  const moods = [
    'All Moods',
    'Relaxing',
    'Exciting',
    'Inspiring',
    'Romantic',
    'Suspenseful',
    'Humorous',
    'Emotional',
    'Motivational',
  ];

  const durations = [
    'All Durations',
    'Under 5 mins',
    '5-15 mins',
    '15-30 mins',
    '30-60 mins',
    'Over 60 mins',
  ];

  const handleFilterChange = (filterType: keyof FilterState, value: string) => {
    onFilterChange({
      ...activeFilters,
      [filterType]: value,
    });
  };

  const clearAllFilters = () => {
    onFilterChange({
      category: 'All Stories',
      language: 'All Languages',
      mood: 'All Moods',
      duration: 'All Durations',
      searchQuery: '',
    });
  };

  const hasActiveFilters =
    activeFilters.category !== 'All Stories' ||
    activeFilters.language !== 'All Languages' ||
    activeFilters.mood !== 'All Moods' ||
    activeFilters.duration !== 'All Durations' ||
    activeFilters.searchQuery !== '';

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6 mb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div className="flex items-center space-x-3">
          <Icon name="FunnelIcon" size={24} className="text-primary" />
          <h2 className="text-xl font-heading font-bold text-text-primary">
            Filter Stories
          </h2>
        </div>

        <div className="flex items-center space-x-3">
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 rounded-lg transition-colors duration-200"
            >
              <Icon name="XMarkIcon" size={18} />
              <span>Clear All</span>
            </button>
          )}

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="md:hidden flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium transition-all duration-200"
          >
            <Icon
              name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
              size={20}
            />
            <span>{isExpanded ? 'Hide' : 'Show'} Filters</span>
          </button>
        </div>
      </div>

      <div
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${
          isExpanded ? 'block' : 'hidden md:grid'
        }`}
      >
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Category
          </label>
          <select
            value={activeFilters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Language
          </label>
          <select
            value={activeFilters.language}
            onChange={(e) => handleFilterChange('language', e.target.value)}
            className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
          >
            {languages.map((language) => (
              <option key={language} value={language}>
                {language}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Mood
          </label>
          <select
            value={activeFilters.mood}
            onChange={(e) => handleFilterChange('mood', e.target.value)}
            className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
          >
            {moods.map((mood) => (
              <option key={mood} value={mood}>
                {mood}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Duration
          </label>
          <select
            value={activeFilters.duration}
            onChange={(e) => handleFilterChange('duration', e.target.value)}
            className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
          >
            {durations.map((duration) => (
              <option key={duration} value={duration}>
                {duration}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;